<template>
  <div class="min-h-screen bg-gradient-to-br from-navy-50 via-white to-primary-50">
    <!-- Hero section -->
    <div class="relative overflow-hidden">
      <!-- Background pattern -->
      <div class="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-700 to-navy-800"></div>
      <div class="absolute inset-0 bg-black/10"></div>
      
      <!-- Decorative elements -->
      <div class="absolute top-0 left-0 w-full h-full">
        <div class="absolute top-20 left-10 w-32 h-32 bg-gold-400/20 rounded-full blur-3xl"></div>
        <div class="absolute bottom-20 right-10 w-40 h-40 bg-accent-400/20 rounded-full blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div class="text-center animate-fade-in">
          <h1 class="hero-title text-white mb-6">
            Notre <span class="bg-gradient-to-r from-gold-400 to-gold-300 bg-clip-text text-transparent">Histoire</span>
          </h1>
          <p class="hero-subtitle text-white/90 max-w-3xl mx-auto">
            Plus de 150 ans de tradition musicale à Stavelot, une saga qui continue de s'écrire
          </p>
        </div>
      </div>
    </div>
    
    <!-- History content -->
    <div class="py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Loading state -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-20">
          <div class="relative">
            <div class="animate-spin rounded-full h-16 w-16 border-4 border-primary-200"></div>
            <div class="animate-spin rounded-full h-16 w-16 border-4 border-primary-600 border-t-transparent absolute top-0 left-0"></div>
          </div>
          <p class="mt-6 text-navy-600 text-lg font-medium">Chargement de notre histoire...</p>
        </div>
        
        <!-- Error state -->
        <div v-else-if="error" class="text-center py-20">
          <div class="card-gradient max-w-md mx-auto">
            <div class="p-8">
              <div class="w-16 h-16 bg-accent-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg class="w-8 h-8 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <h3 class="text-xl font-bold text-navy-800 mb-4">Oups !</h3>
              <p class="text-navy-600 mb-6">Impossible de charger l'historique. Veuillez réessayer plus tard.</p>
              <button @click="loadHistory" class="btn-primary">Réessayer</button>
            </div>
          </div>
        </div>
        
        <!-- History timeline -->
        <div v-else-if="historyEntries.length > 0" class="relative">
          <!-- Timeline line -->
          <div class="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-200 via-primary-300 to-gold-200 rounded-full"></div>
          
          <div class="space-y-16">
            <div 
              v-for="(entry, index) in historyEntries" 
              :key="entry.id"
              class="relative group"
            >
              <div class="flex items-start">
                <!-- Year badge -->
                <div class="flex-shrink-0 relative z-10">
                  <div class="bg-gradient-to-br from-primary-600 to-primary-700 text-white w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-lg shadow-xl group-hover:scale-110 transition-all duration-300 group-hover:shadow-2xl">
                    {{ entry.year || '?' }}
                  </div>
                  <!-- Glow effect -->
                  <div class="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                </div>
                
                <!-- Content -->
                <div class="ml-8 flex-1">
                  <div class="card-gradient group-hover:shadow-2xl transition-all duration-500 group-hover:scale-[1.02]">
                    <div class="p-8">
                      <h3 class="text-3xl font-bold text-navy-800 mb-6 group-hover:text-primary-600 transition-colors duration-300">
                        {{ entry.title }}
                      </h3>
                      
                      <div v-if="entry.image_url" class="mb-8">
                        <div class="relative overflow-hidden rounded-2xl shadow-2xl group-hover:shadow-3xl transition-all duration-500">
                          <img 
                            :src="entry.image_url" 
                            :alt="entry.title"
                            class="w-full object-cover transition-all duration-500 hover:scale-105 history-image"
                            :style="{ 
                              height: 'auto',
                              minHeight: '200px',
                              maxHeight: '500px',
                              objectFit: 'cover'
                            }"
                            @load="onImageLoad"
                            @error="onImageError"
                          />
                          <!-- Modern overlay -->
                          <div class="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          <!-- Shine effect -->
                          <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                        </div>
                      </div>
                      
                      <div class="prose prose-lg max-w-none">
                        <p class="text-navy-600 leading-relaxed whitespace-pre-line text-lg">
                          {{ entry.content }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Empty state -->
        <div v-else class="text-center text-gray-500">
          <p>Aucune entrée d'historique disponible pour le moment.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { historyService } from '../services/historyService'
import { canDisplayImage as checkImageUrl } from '../utils/imageUtils'

export default {
  name: 'History',
  data() {
    return {
      historyEntries: [],
      loading: true,
      error: false
    }
  },
  async mounted() {
    await this.loadHistory()
  },
  methods: {
    async loadHistory() {
      try {
        this.loading = true
        this.error = false
        this.historyEntries = await historyService.getHistoryEntries()
        // Sort by year descending
        this.historyEntries.sort((a, b) => (b.year || 0) - (a.year || 0))
      } catch (error) {
        console.error('Error loading history:', error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    
    onImageLoad(event) {
      // Laisser l'image s'afficher avec son ratio naturel
      const img = event.target
      
      // Calculer le ratio de l'image
      const aspectRatio = img.naturalWidth / img.naturalHeight
      
      // Calculer la hauteur optimale en fonction de la largeur du conteneur
      const containerWidth = img.parentElement.offsetWidth
      let optimalHeight = containerWidth / aspectRatio
      
      // Appliquer des limites min/max
      optimalHeight = Math.max(200, Math.min(500, optimalHeight))
      
      // Appliquer la hauteur calculée
      img.style.height = `${optimalHeight}px`
    },
    
    onImageError(event) {
      // Masquer l'image si elle ne peut pas être chargée
      const img = event.target
      const container = img.parentElement
      container.style.display = 'none'
    },
    
    canDisplayImage(url) {
      return checkImageUrl(url)
    }
  }
}
</script>

<style scoped>
/* Styles pour les images d'historique */
.history-image {
  transition: all 0.3s ease;
}

.history-image:hover {
  transform: scale(1.02);
}

/* Responsive design pour les images */
@media (max-width: 768px) {
  .history-image {
    min-height: 150px !important;
    max-height: 300px !important;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .history-image {
    min-height: 200px !important;
    max-height: 400px !important;
  }
}

@media (min-width: 1025px) {
  .history-image {
    min-height: 250px !important;
    max-height: 500px !important;
  }
}
</style>
